<template>
	<Accordion
		:title="title"
		:open="open"
		:icon="icon"
		:icon-hint="transUcfirst('tag_group.visible_in_player')"
		:disabled="disabled"
		:disabled-hint="disabledHint"
		@toggle="$emit('toggle', $event)"
	>
		<div
			v-if="info"
			class="info"
			:class="{'is-mobile': isMobileWebApp}"
		>
			<div class="info--lead">
				<Icon
					icon-name="c-info"
					size="sm"
				/>
			</div>
			<div class="info--body">
				{{ info }}
			</div>
		</div>
		<div class="participants-container">
			<!-- in player context -->
			<ParticipantTags
				v-if="!multi"
				:sport-name="sportName"
				:participant="participantsOptions[0]"
				:tags="tags"
				:squared-buttons="squaredButtons"
				:align-buttons="buttonsAlignment"
				:show-header="false"
				@tag-click="(tag) => $emit('click', { tag, participant: participantsOptions[0] })"
			/>
			<!-- in team context -->
			<ParticipantTags
				v-for="participant in participantsOptions"
				v-else
				:key="`participant-${participant.key}`"
				class="participant"
				:sport-name="sportName"
				:participant="participant"
				:tags="tags"
				:squared-buttons="squaredButtons"
				:align-buttons="buttonsAlignment"
				:show-header="true"
				@tag-click="(tag) => $emit('click', { tag, participant })"
			/>
		</div>
	</Accordion>
</template>

<script>
import { mapGetters } from 'vuex';
import Accordion from '@components/accordion/Accordion';
import ParticipantTags from '@videotools/js/one/components/TagCenter/ParticipantTags';

export default {
	name:       'ParticipantTagsSection',
	components: { ParticipantTags, Accordion },
	props:      {
		title: {
			type: String,
		},
		tags: {
			type:    Array,
			default: () => [],
		},
		athlete: {
			type:     Object,
			required: false,
		},
		open: {
			type:    Boolean,
			default: false,
		},
		participants: {
			type:    Array,
			default: () => [],
		},
		squaredButtons: {
			type:    Boolean,
			default: false,
		},
		icon: {
			type:     String,
			required: false,
		},
		disabled: {
			type:    Boolean,
			default: false,
		},
		disabledHint: {
			type:    String,
			default: null,
		},
		info: {
			type: String,
		},
	},
	computed: {
		...mapGetters({
			isMobileWebApp:          'app/isMobileWebApp',
			sportParticipantType:    'event/participantType',
			sportName:               'event/sportName',
			isSequentialCompetition: 'event/isSequentialCompetition',
		}),
		labels() {
			return {
				tagButtons:      this.trans('one.tag_buttons'),
				matchTags:       this.trans('one.match_tags'),
				competitionTags: this.trans('one.competition_tags'),
				customTags:      this.isSequentialCompetition ? this.trans('one.run') : this.transchoice('one.custom_tags', 0),
				tags:            this.tagsLabel,
				markIn:          this.isSequentialCompetition ? this.trans('one.start') : this.trans('one.mark_in'),
				markOut:         this.isSequentialCompetition ? this.trans('one.finish') : this.trans('one.mark_out'),
			};
		},
		participantsOptions() {
			return this.participants.filter(Boolean).map(participant => ({
				key:      participant.id,
				id:       participant.id,
				label:    participant.club ? participant.club.name : participant.name,
				code:     participant.club ? participant.club.code : null,
				imageUrl: participant.imageUrl,
			}));
		},
		multi() {
			return this.participants.length > 1;
		},
		buttonsAlignment() {
			if (this.isMobileWebApp) {
				return 'rows';
			}
			if (this.multi) {
				return 'one-column';
			}
			return 'two-columns';
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import "@videotools/sass/_fonts.scss";

$gap: $sp4;

.participants-container {
	display: flex;
	gap: $gap;
}

.participant {
	flex-basis: calc(50% - #{$gap} / 2);
	max-width: calc(50% - #{$gap} / 2);
}

.info {
	display: flex;
	align-items: flex-start;
	color: var(--color-grey-300);
	@include text-sm-2;
	margin-bottom: 24px;
	margin-top: -16px;

	&.is-mobile {
		margin-top: 0;
	}

	&--lead {
		padding: 3px 8px 3px 0;
	}
}
</style>
